"use client";

import { Slot } from "@radix-ui/react-slot";
import type { ComponentProps } from "react";
import { cn } from "@/lib/cn";

/**
 * The app's button.
 *
 * Ported from the legacy `Forms/CustomButton`, not `ui/Button`. The latter was a
 * copied shadcn button whose variants referenced `primary-foreground`,
 * `destructive`, `accent`, `secondary` and `input` — five tokens the legacy
 * Tailwind config never defined, so those variants resolved to layout classes and
 * no colour at all. These variants are built from `--primary` and the
 * `dynamic-text` utility, which do exist.
 */
const VARIANTS = {
  primary: cn(
    "border border-[var(--primary)] bg-[var(--primary)] dynamic-text",
    "hover:bg-white hover:text-black dark:hover:bg-black dark:hover:text-white"
  ),
  secondary: cn(
    "border border-[var(--primary)] bg-white text-black dark:bg-black dark:text-off-white",
    "hover:bg-gray-100 dark:hover:bg-gray-900"
  ),
  /**
   * A neutral bordered button, for controls that shouldn't compete with the
   * brand colour — the pagination is the case.
   *
   * The legacy `outline` variant was `border border-input bg-background
   * hover:bg-accent hover:text-accent-foreground`, and none of those four tokens
   * existed in its Tailwind config, so what actually rendered was a transparent
   * box with Tailwind's default `gray-200` border and no hover at all. That is
   * what this reproduces, with the hover left to the caller as before.
   */
  outline: "border border-gray-200 bg-transparent dark:border-gray-700",
  ghost: "font-medium hover:bg-gray-50 dark:hover:bg-gray-900",
} as const;

const SIZES = {
  default: "h-[2.375rem] gap-1.5 rounded-lg px-2.5 text-base",
  xs: "h-7 gap-1 rounded-md px-2 text-xs",
  sm: "h-8 gap-1 rounded-md px-3 text-sm",
  lg: "h-10 gap-1.5 rounded-md px-6",
  "icon-sm": "size-7 rounded-md",
  "icon-lg": "size-10 rounded-md",
} as const;

export type ButtonVariant = keyof typeof VARIANTS;
export type ButtonSize = keyof typeof SIZES;

export interface ButtonProps extends ComponentProps<"button"> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Render the child element instead of a <button>, keeping the styling. */
  asChild?: boolean;
  /** Held-down state, styled with the brand colour. */
  pressed?: boolean;
}

export function Button({
  className,
  variant = "primary",
  size = "default",
  asChild = false,
  pressed,
  disabled,
  ...props
}: ButtonProps) {
  // `asChild` is what lets a link carry button styling without nesting an <a>
  // inside a <button>, which is invalid and unreachable by keyboard.
  const Component = asChild ? Slot : "button";

  return (
    <Component
      className={cn(
        "inline-flex shrink-0 items-center justify-center text-center",
        "transition-colors duration-150",
        "focus:z-10 focus:outline-none focus:ring-4 focus:ring-gray-100 dark:focus:ring-gray-800",
        VARIANTS[variant],
        SIZES[size],
        pressed && "bg-[var(--primary)] text-[var(--dynamic-text-color)]",
        disabled && "cursor-not-allowed opacity-50",
        className
      )}
      // Only when the caller says this is a toggle: defaulting it would announce
      // every button in the app as a two-state control.
      aria-pressed={pressed}
      disabled={disabled}
      {...props}
    />
  );
}
